import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Pause, SkipForward, ArrowLeft, User, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatPage() {
  const [userId, setUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [memory, setMemory] = useState(null);
  const [careerState, setCareerState] = useState({
    name: null,
    age: null,
    king_or_queen: null,
    discovery_questions: [],
    core_values_questions: [],
    going_deeper_active: false,
    phase: 'introduction'
  });
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Extract email from URL query parameter (?email=user@email.com)
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    
    // Also check sessionStorage as fallback
    const storedUserId = sessionStorage.getItem('serenity_user_id');
    
    // Email from URL takes priority, then sessionStorage
    const userEmail = emailParam || storedUserId;
    
    if (!userEmail) {
      toast.error('Email required. Please return to the signup page.');
      navigate('/');
      return;
    }
    
    // Store email in sessionStorage for future use
    if (emailParam) {
      sessionStorage.setItem('serenity_user_id', emailParam);
    }
    
    setUserId(userEmail);
    initializeSession(userEmail);
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeSession = async (email) => {
    console.log('[INIT] Starting for:', email);
    try {
      console.log('[INIT] Calling API:', `${API}/serenity/init`);
      const response = await axios.post(`${API}/serenity/init`, { user_id: email }, {
        timeout: 30000  // 30 second timeout for init
      });
      console.log('[INIT] Response received:', response.data);
      
      const { status, memory: userMemory } = response.data;
      
      setMemory(userMemory);
      
      // Extract career state from memory
      const careerState = userMemory?.career_fix_state || {};
      setCareerState(careerState);
      
      // Check if user has progress
      const userName = careerState.name;
      const discoveryProgress = careerState.discovery_questions?.length || 0;
      const hasProgress = discoveryProgress > 0 || userName;
      
      console.log('[INIT] User status:', status, 'Name:', userName, 'Progress:', discoveryProgress);
      
      if (status === 'new' || !hasProgress) {
        // NEW USER - Show fresh welcome message (no King/Queen)
        const welcomeMsg = {
          id: Date.now(),
          role: 'assistant',
          content: `Welcome! I'm Serenity, your Pathways Companion. Before we begin, I'd love to know:\n\nWhat's your name?\nHow old are you?`,
        };
        setMessages([welcomeMsg]);
        console.log('[INIT] New user - welcome message set');
      } else {
        // RETURNING USER - Show welcome back
        console.log('[INIT] Returning user:', userName, 'Progress:', discoveryProgress);
        
        // Cap progress display at 10
        const displayProgress = Math.min(discoveryProgress, 10);
        const nextQ = discoveryProgress >= 10 ? "complete" : discoveryProgress + 1;
        
        const welcomeBackMsg = {
          id: Date.now(),
          role: 'assistant',
          content: discoveryProgress >= 10 
            ? `Welcome back, ${userName}! You've completed all 10 Discovery questions. Ready to generate your pathway?`
            : `Welcome back, ${userName}! You're at Discovery Question ${nextQ} of 10. Ready to continue?`,
        };
        setMessages([welcomeBackMsg]);
        toast.success(`Resuming your journey - Discovery ${displayProgress}/10`);
      }
      
      setLoading(false);
      console.log('[INIT] Complete - loading set to false');
    } catch (error) {
      console.error('[INIT] Error:', error);
      console.error('[INIT] Error details:', error.response?.data || error.message);
      toast.error(`Failed to initialize session: ${error.message}`);
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || typing) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setTyping(true);

    try {
      const response = await axios.post(`${API}/serenity/message`, {
        user_id: userId,
        message: userMessage.content,
        session_id: sessionId,
      });

      const { 
        response: aiResponse, 
        session_id, 
        phase, 
        questions_completed,
        discovery_progress,
        core_values_progress,
        career_fix_state 
      } = response.data;
      
      if (!sessionId) {
        setSessionId(session_id);
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponse,
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update memory and career state
      if (memory) {
        setMemory(prev => ({
          ...prev,
          current_phase: phase,
          questions_completed: questions_completed,
          career_fix_state: career_fix_state
        }));
      }
      
      if (career_fix_state) {
        console.log('[MESSAGE] Updating career state:', {
          discovery: career_fix_state.discovery_questions?.length || 0,
          core_values: career_fix_state.core_values_questions?.length || 0,
          phase: career_fix_state.phase
        });
        setCareerState(career_fix_state);
      }
    } catch (error) {
      console.error('Message error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSkip = async () => {
    // Check backend phase to determine if we're at a phase boundary
    const currentPhase = careerState?.phase || 'introduction';
    
    // At phase boundaries, the backend sets phase to "transition"
    // This is when we should show "Skip to Phase II/III" and send continue message
    const atTransition = currentPhase === 'transition';
    const atPhase1Boundary = atTransition && discoveryProgress === 10 && coreValuesProgress === 0;
    const atPhase2Boundary = atTransition && coreValuesProgress === 10 && goingDeeperProgress === 0;
    
    // Determine message to send
    let messageToSend = 'I\'d like to skip this question';
    if (atPhase1Boundary) {
      messageToSend = 'continue to phase 2';
    } else if (atPhase2Boundary) {
      messageToSend = 'continue to phase 3';
    }
    
    const skipMsg = {
      id: Date.now(),
      role: 'user',
      content: 'skip',
    };
    setMessages(prev => [...prev, skipMsg]);
    setInputValue('');
    setTyping(true);

    try {
      const response = await axios.post(`${API}/serenity/message`, {
        user_id: userId,
        message: messageToSend,
        session_id: sessionId,
      });

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update memory and career state
      if (memory) {
        setMemory(prev => ({
          ...prev,
          questions_completed: response.data.questions_completed,
          current_phase: response.data.phase,
          career_fix_state: response.data.career_fix_state
        }));
      }
      
      if (response.data.career_fix_state) {
        setCareerState(response.data.career_fix_state);
      }
    } catch (error) {
      toast.error('Failed to skip question');
    } finally {
      setTyping(false);
    }
  };

  const handleGenerateSummary = async () => {
    setTyping(true);
    
    // Show informative toast about wait time
    toast.info('Generating your complete Serenity Pathway... This may take 2-5 minutes for the full pathway. Please wait, your pathway is worth it! ✨', {
      duration: 10000
    });
    
    try {
      const response = await axios.post(`${API}/serenity/generate-summary`, {
        user_id: userId,
        message: 'generate summary',
      }, {
        timeout: 360000  // 6 minutes timeout for pathway generation (V3 can take 2-5 mins)
      });

      toast.success('Your complete Serenity Pathway is ready!');
      
      // Navigate to summary page
      setTimeout(() => {
        navigate('/summary');
      }, 1000);
    } catch (error) {
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || 'Complete at least 10 discovery questions before generating your pathway';
        toast.error(detail);
      } else if (error.response?.status === 503) {
        // API service temporarily unavailable - encourage retry
        const detail = error.response?.data?.detail || 'AI service is experiencing high demand';
        toast.error(detail, { duration: 8000 });
        setTimeout(() => {
          toast.info('💡 Click "Generate Pathway" again - it usually works on the second try!', { duration: 10000 });
        }, 2000);
      } else if (error.code === 'ECONNABORTED') {
        // Timeout error
        toast.error('Generation is taking longer than expected. Please try again - your progress is saved.', {
          duration: 8000
        });
      } else {
        toast.error('Failed to generate pathway. Please try again.');
      }
    } finally {
      setTyping(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-serenity-accent-blue border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-serenity-secondary">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Calculate progress from career state
  const discoveryProgress = careerState?.discovery_questions?.length || 0;
  const coreValuesProgress = careerState?.core_values_questions?.length || 0;
  const goingDeeperProgress = careerState?.going_deeper_questions?.length || 0;
  const totalProgress = discoveryProgress + coreValuesProgress + goingDeeperProgress;
  const isGoingDeeperActive = careerState?.going_deeper_active || (discoveryProgress >= 10 && coreValuesProgress >= 10);
  const isDiscoveryComplete = discoveryProgress >= 10;
  const isCoreValuesComplete = coreValuesProgress >= 10;
  const userName = careerState?.name || '';

  // Determine current phase display - SEPARATE PROGRESS FOR EACH PHASE
  const getPhaseDisplay = () => {
    if (isGoingDeeperActive) {
      return { label: 'Going Deeper Mode', sublabel: 'Unlimited exploration active', progress: 100 };
    }
    if (isCoreValuesComplete) {
      return { label: 'Core Values Complete', sublabel: '10/10 ✓', progress: 100 };
    }
    if (isDiscoveryComplete) {
      // Phase II - Core Values (0-100% based on CV progress)
      return { label: 'Core Values Progress', sublabel: `${coreValuesProgress}/10`, progress: (coreValuesProgress / 10) * 100 };
    }
    // Phase I - Discovery (0-100% based on Discovery progress)
    return { label: 'Discovery Progress', sublabel: `${discoveryProgress}/10`, progress: (discoveryProgress / 10) * 100 };
  };

  const phaseDisplay = getPhaseDisplay();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-serenity-border sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-serenity-primary-blue">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mentor-chat-3/artifacts/5i6s80b4_Gemini_Generated_Image_13pb5e13pb5e13pb-2.png" 
                  alt="Serenity Avatar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-playfair text-xl text-serenity-primary-blue font-semibold">Serenity</h2>
                <p className="text-xs text-serenity-muted">Your Personal Pathway Companion</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right mr-4">
                {isGoingDeeperActive ? (
                  <p className="text-xs font-semibold text-serenity-accent-blue flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Going Deeper Mode ✓
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-serenity-muted">{phaseDisplay.label}</p>
                    <p className="text-sm font-semibold text-serenity-primary-blue">
                      {phaseDisplay.sublabel}
                    </p>
                  </>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-serenity-secondary hover:text-serenity-primary-blue hover:bg-serenity-light-blue"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Progress Bar - SEPARATE FOR EACH PHASE (0-100% per phase) */}
          {!isGoingDeeperActive && (
            <div className="mt-3 h-3 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-serenity-primary-blue transition-all duration-300 ease-out rounded-full"
                style={{ width: `${phaseDisplay.progress}%` }}
              />
            </div>
          )}
          
          {/* Phase Completion Messages */}
          {discoveryProgress === 10 && coreValuesProgress === 0 && (
            <div className="mt-3 text-center">
              <p className="text-sm font-semibold text-serenity-accent-blue">
                ✨ Discovery Phase Complete! Ready to see your pathway or continue to Phase II?
              </p>
            </div>
          )}
          {discoveryProgress === 10 && coreValuesProgress === 10 && goingDeeperProgress === 0 && (
            <div className="mt-3 text-center">
              <p className="text-sm font-semibold text-serenity-accent-blue">
                ✨ Core Values Phase Complete! Continue to Phase III or generate your enhanced pathway?
              </p>
            </div>
          )}
          {isGoingDeeperActive && (
            <div className="mt-3 text-center bg-serenity-light-blue rounded-lg p-2">
              <p className="text-xs text-serenity-secondary">
                <span className="font-semibold text-serenity-accent-blue">🌟 Going Deeper Active</span>
                <br />
                Unlimited personalized exploration. Ask anything about your pathway!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto chat-scroll px-6 py-8 bg-white">
        <div className="max-w-3xl mx-auto space-y-6">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-full overflow-hidden shadow-md border-2 border-serenity-primary-blue flex-shrink-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mentor-chat-3/artifacts/5i6s80b4_Gemini_Generated_Image_13pb5e13pb5e13pb-2.png" 
                      alt="Serenity Avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div
                  data-testid={`message-${message.role}`}
                  className={`max-w-[75%] ${
                    message.role === 'assistant'
                      ? 'bg-serenity-light-blue text-serenity-secondary rounded-2xl rounded-tl-none'
                      : 'bg-white text-serenity-secondary rounded-2xl rounded-tr-none border border-serenity-border'
                  } p-6 shadow-sm`}
                >
                  <div className="markdown-content">
                    <ReactMarkdown
                      components={{
                        p: ({ node, children }) => {
                          // Convert children to string for checking
                          const text = children?.toString() || '';
                          
                          // Only process assistant messages
                          if (message.role !== 'assistant') {
                            return <p className="mb-3">{children}</p>;
                          }
                          
                          // Check if this contains a question mark
                          const hasQuestionMark = text.includes('?');
                          if (!hasQuestionMark || text.includes('**') || text.includes('•')) {
                            return <p className="mb-3">{children}</p>;
                          }
                          
                          // Check if this is an acknowledgment paragraph
                          const firstSentence = text.split(/[.!]/)[0];
                          const isAcknowledgment = /great|wonderful|thank|love|appreciate|nice|good|perfect|beautiful|amazing/i.test(firstSentence);
                          
                          if (isAcknowledgment) {
                            return <p className="mb-3">{children}</p>;
                          }
                          
                          // This is a question paragraph - highlight all question sentences
                          // Check if entire paragraph is a question
                          const paragraphText = text.trim();
                          if (paragraphText.endsWith('?') && !paragraphText.includes('(') && !paragraphText.includes('Guidance:')) {
                            // Entire paragraph is a question - highlight it all
                            return <p className="text-serenity-primary-blue font-medium mb-3 leading-relaxed">{children}</p>;
                          }
                          
                          // Mixed content - leave as regular
                          return <p className="mb-3">{children}</p>;
                        }
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
                {message.role === 'user' && (
                  <div className="w-10 h-10 bg-serenity-primary-blue rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <User className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {typing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-4"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden shadow-md border-2 border-serenity-primary-blue flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mentor-chat-3/artifacts/5i6s80b4_Gemini_Generated_Image_13pb5e13pb5e13pb-2.png" 
                  alt="Serenity Avatar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-serenity-light-blue rounded-2xl rounded-tl-none p-6 shadow-sm">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-serenity-accent-blue rounded-full animate-typing-dot" />
                  <div className="w-2 h-2 bg-serenity-accent-blue rounded-full animate-typing-dot" />
                  <div className="w-2 h-2 bg-serenity-accent-blue rounded-full animate-typing-dot" />
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-serenity-border sticky bottom-0 shadow-sm">
        <div className="max-w-3xl mx-auto p-6">
          {/* Phase Completion Banners */}
          {discoveryProgress === 10 && coreValuesProgress === 0 && (
            <div className="mb-4 p-4 bg-serenity-light-blue rounded-lg border border-serenity-accent-blue text-center">
              <p className="text-lg font-semibold text-serenity-primary-blue">
                ✨ Discovery Phase Complete!
              </p>
              <p className="text-sm text-serenity-secondary mt-1">
                You can generate your pathway now or continue to Phase II for deeper insights.
              </p>
            </div>
          )}
          {discoveryProgress === 10 && coreValuesProgress === 10 && goingDeeperProgress === 0 && (
            <div className="mb-4 p-4 bg-serenity-light-blue rounded-lg border border-serenity-accent-blue text-center">
              <p className="text-lg font-semibold text-serenity-primary-blue">
                ✨ Core Values Phase Complete!
              </p>
              <p className="text-sm text-serenity-secondary mt-1">
                You can generate your enhanced pathway now or continue to Phase III for the deepest insights.
              </p>
            </div>
          )}
          {isGoingDeeperActive && (
            <div className="mb-4 p-4 bg-gradient-to-r from-serenity-light-blue to-blue-50 rounded-lg border border-serenity-accent-blue">
              <p className="text-lg font-semibold text-serenity-primary-blue flex items-center justify-center gap-2 mb-3">
                <Sparkles className="w-5 h-5" />
                Going Deeper Mode Active
              </p>
              <p className="text-sm text-serenity-secondary text-center mb-4">
                Both phases complete! Explore deeper or generate your comprehensive pathway.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("Tell me more about my ideal career paths")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-careers-btn"
                >
                  🎯 Career Paths
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("What are my hidden strengths?")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-strengths-btn"
                >
                  💪 Strengths
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("What's my ideal work environment?")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-environment-btn"
                >
                  🏢 Work Style
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("What barriers might I face and how can I overcome them?")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-barriers-btn"
                >
                  🚧 Barriers
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("Give me concrete next steps I can take this week")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-nextsteps-btn"
                >
                  📋 Next Steps
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInputValue("What business ideas match my profile?")}
                  className="text-xs text-serenity-secondary hover:bg-serenity-light-blue"
                  data-testid="explore-business-btn"
                >
                  🚀 Business Ideas
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mb-3 flex-wrap">
            <Button
              data-testid="skip-button"
              onClick={handleSkip}
              variant="outline"
              size="sm"
              className="text-serenity-secondary hover:text-serenity-primary-blue border-serenity-border hover:bg-serenity-light-blue"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              {careerState?.phase === 'transition' && discoveryProgress === 10 && coreValuesProgress === 0 ? 'Skip to Phase II' : 
               careerState?.phase === 'transition' && coreValuesProgress === 10 && goingDeeperProgress === 0 ? 'Skip to Phase III' : 
               'Skip'}
            </Button>
            
            {/* Generate Pathway Button - Phase-based button text (Fix #6) */}
            {discoveryProgress >= 10 ? (
              <Button
                data-testid="generate-summary-button"
                onClick={handleGenerateSummary}
                disabled={typing}
                size="sm"
                className="bg-serenity-accent-blue text-white hover:bg-serenity-primary-blue"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {goingDeeperProgress >= 10 ? 'Generate Complete Pathway (30Q)' : coreValuesProgress > 0 ? 'Generate Full Pathway' : 'Generate Pathway'}
              </Button>
            ) : (
              <Button
                onClick={() => toast.info(`Complete all 10 Discovery questions first. You're at ${discoveryProgress}/10.`)}
                variant="outline"
                size="sm"
                disabled
                className="text-serenity-muted border-serenity-border text-xs"
              >
                Complete Discovery Phase ({discoveryProgress}/10)
              </Button>
            )}
          </div>
          
          <div className="flex gap-3">
            <Input
              data-testid="chat-input"
              type="text"
              placeholder="Share your thoughts..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={typing}
              className="flex-1 h-12 bg-white border-serenity-border focus:ring-2 focus:ring-serenity-accent-blue focus:border-serenity-accent-blue"
            />
            <Button
              data-testid="send-button"
              onClick={sendMessage}
              disabled={!inputValue.trim() || typing}
              className="h-12 px-6 bg-serenity-primary-blue text-white hover:bg-[#1a3fb3] rounded-full transition-all duration-300 shadow-md hover:shadow-lg font-semibold"
            >
              <Send className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}